import { Node, mergeAttributes } from '@tiptap/react';

export type CalloutVariant = 'info' | 'warning' | 'success' | 'danger';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      /** Insert a callout block of the given variant after the current block. */
      insertCallout: (variant: CalloutVariant) => ReturnType;
    };
  }
}

export const CalloutExtension = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: 'info' as CalloutVariant,
        parseHTML: (el) => el.getAttribute('data-callout-variant') as CalloutVariant | null,
        renderHTML: (attrs) => ({ 'data-callout-variant': attrs.variant }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-callout-variant]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = HTMLAttributes ?? {};
    const variant = (attrs['data-callout-variant'] as CalloutVariant) ?? 'info';
    return [
      'div',
      mergeAttributes(attrs, {
        class: `nula-editor-callout nula-editor-callout--${variant}`,
      }),
      0,
    ];
  },

  renderMarkdown(node, helpers) {
    return helpers
      .renderChildren(node, '\n\n')
      .trimEnd()
      .split('\n')
      .map((line) => (line === '' ? '>' : `> ${line}`))
      .join('\n');
  },

  addCommands() {
    return {
      insertCallout:
        (variant: CalloutVariant) =>
        ({ chain }: { chain: () => import('@tiptap/core').ChainedCommands }) => {
          return chain()
            .focus()
            .insertContent({
              type: 'callout',
              attrs: { variant },
              content: [{ type: 'paragraph' }],
            })
            .run();
        },
    };
  },
});
